import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/FirebaseAuthContext';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc, onSnapshot, setDoc, updateDoc, orderBy } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/useToast';
import { Trophy, TrendingUp, Gift, Radio, Medal, Users, Timer, ChevronDown, ChevronUp, FileText, Sparkles, Music2, Share2, Copy } from 'lucide-react';

const ArtistCompetitionPage = () => {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [competitionEnabled, setCompetitionEnabled] = useState(false);
  const [myPoints, setMyPoints] = useState(0);
  const [mySelfPoints, setMySelfPoints] = useState(0);
  const [myReferralPoints, setMyReferralPoints] = useState(0);
  const [myRank, setMyRank] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [competitionDetails, setCompetitionDetails] = useState({
    prize: 'Win an all-expenses paid trip to the home of music; Nashville!',
    rules: [
      '+2 points for each song request you complete (unlimited)',
      '+1 point for each song your referred artist completes (unlimited)',
      'The artist with the most points wins the prize'
    ],
    startAt: new Date('2025-11-10T00:00:00Z').toISOString(),
    endAt: null
  });
  const [activeTab, setActiveTab] = useState('leaderboard');
  const [nowTs, setNowTs] = useState(Date.now());
  const [showTerms, setShowTerms] = useState(false);
  const [competitionDetailsExpanded, setCompetitionDetailsExpanded] = useState(true);
  const [preferenceLoaded, setPreferenceLoaded] = useState(false);
  const [showPointsBreakdown, setShowPointsBreakdown] = useState(false);
  const [referralBreakdown, setReferralBreakdown] = useState([]);
  const [referralCode, setReferralCode] = useState(null);
  const [referralUrl, setReferralUrl] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/admin');
      return;
    }
    
    setLoading(true);
    
    // Load user's collapse preference
    const loadPreference = async () => {
      if (!user) return;
      try {
        // Check localStorage first (for immediate persistence)
        const localStoragePref = localStorage.getItem('ysr_competition_details_expanded');
        if (localStoragePref !== null) {
          setCompetitionDetailsExpanded(localStoragePref === 'true');
          setPreferenceLoaded(true);
        }
        
        // Then check Firestore profile (for cross-device sync)
        const profileRef = doc(db, 'profiles', user.uid);
        const profileDoc = await getDoc(profileRef);
        if (profileDoc.exists()) {
          const data = profileDoc.data();
          // Use Firestore value if available, otherwise default to true
          if (data.competitionDetailsExpanded !== undefined) {
            setCompetitionDetailsExpanded(data.competitionDetailsExpanded);
            // Sync to localStorage
            localStorage.setItem('ysr_competition_details_expanded', String(data.competitionDetailsExpanded));
          } else {
            // Default to true if no preference exists
            setCompetitionDetailsExpanded(true);
            localStorage.setItem('ysr_competition_details_expanded', 'true');
          }
        } else {
          // No profile yet, use localStorage or default to true
          if (localStoragePref === null) {
            setCompetitionDetailsExpanded(true);
            localStorage.setItem('ysr_competition_details_expanded', 'true');
          }
        }
      } catch (error) {
        console.error('Error loading preference:', error);
        // Default to expanded on error, use localStorage if available
        const localStoragePref = localStorage.getItem('ysr_competition_details_expanded');
        setCompetitionDetailsExpanded(localStoragePref !== null ? localStoragePref === 'true' : true);
      } finally {
        setPreferenceLoaded(true);
      }
    };
    
    loadPreference();
    
    // Subscribe to real-time updates for competition settings
    const unsubscribeCompetition = onSnapshot(doc(db, 'competitions', 'current'), (doc) => {
      if (doc.exists()) {
        setCompetitionEnabled(doc.data().enabled);
        setCompetitionDetails({
          prize: doc.data().prize || competitionDetails.prize,
          rules: doc.data().rules || competitionDetails.rules,
          startAt: doc.data().startAt || competitionDetails.startAt,
          endAt: doc.data().endAt || competitionDetails.endAt
        });
      }
      setLoading(false);
    });

    // Subscribe to real-time updates for competition points (leaderboard)
    const leaderboardQuery = query(
      collection(db, 'competitionPoints'),
      where('totalPoints', '>', 0),
      orderBy('totalPoints', 'desc')
    );
    
    const unsubscribeLeaderboard = onSnapshot(
      leaderboardQuery,
      async (snapshot) => {
        console.log(`📊 Artist Leaderboard snapshot received: ${snapshot.docs.length} documents`);
        try {
          const leaderboardData = [];
          for (const docSnap of snapshot.docs) {
            const data = docSnap.data();
            const profileDoc = await getDoc(doc(db, 'profiles', data.userId || docSnap.id));
            if (profileDoc.exists()) {
              const profileData = profileDoc.data();
              leaderboardData.push({
                userId: data.userId || docSnap.id,
                name: profileData.artist_name || profileData.karaoke_name || 'Unknown',
                email: profileData.email || '',
                points: data.totalPoints || 0
              });
            }
          }

          // Sort by points (should already be sorted by Firestore, but ensure it)
          leaderboardData.sort((a, b) => b.points - a.points);
          setLeaderboard(leaderboardData);
          
          const myRankIndex = leaderboardData.findIndex(item => item.userId === user.uid);
          setMyRank(myRankIndex >= 0 ? myRankIndex + 1 : null);
          console.log(`📊 Updated leaderboard with ${leaderboardData.length} artists, my rank: ${myRankIndex >= 0 ? myRankIndex + 1 : 'N/A'}`);
        } catch (error) {
          console.error('Error updating leaderboard:', error);
        }
      },
      (error) => {
        console.error('Error subscribing to leaderboard:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
      }
    );

    // Subscribe to real-time updates for user's own points
    const unsubscribeUserPoints = onSnapshot(
      doc(db, 'competitionPoints', user.uid),
      async (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setMyPoints(data.totalPoints || 0);
          setMySelfPoints(data.points_song_completed || data.songsCompleted || 0);
          setMyReferralPoints(data.points_referral || data.referralsCompleted || 0);
        } else {
          setMyPoints(0);
          setMySelfPoints(0);
          setMyReferralPoints(0);
        }
      }
    );

    // Load referral code
    const loadReferralCode = async () => {
      try {
        const { getUserReferralCode } = await import('@/lib/referralSystem');
        const code = await getUserReferralCode(user.uid);
        if (code) {
          setReferralCode(code);
          const { config } = await import('@/lib/config');
          setReferralUrl(`${config.getBaseUrl()}/signup?ref=${code}`);
        }
      } catch (error) {
        console.error('Error loading referral code:', error);
      }
    };
    loadReferralCode();

    return () => {
      unsubscribeCompetition();
      unsubscribeLeaderboard();
      unsubscribeUserPoints();
    };
  }, [user, navigate]);

  // Save collapse preference
  const toggleCompetitionDetails = async () => {
    const newValue = !competitionDetailsExpanded;
    setCompetitionDetailsExpanded(newValue);
    
    // Save to localStorage immediately for instant persistence
    localStorage.setItem('ysr_competition_details_expanded', String(newValue));
    
    if (user) {
      try {
        const profileRef = doc(db, 'profiles', user.uid);
        await updateDoc(profileRef, {
          competitionDetailsExpanded: newValue
        });
      } catch (error) {
        console.error('Error saving preference:', error);
        // If update fails, try setDoc with merge
        try {
          const profileRef = doc(db, 'profiles', user.uid);
          await setDoc(profileRef, {
            competitionDetailsExpanded: newValue
          }, { merge: true });
        } catch (setError) {
          console.error('Error saving preference with setDoc:', setError);
        }
      }
    }
  };

  // Fetch referral breakdown when expanded
  const fetchReferralBreakdown = async () => {
    if (!user) return;
    
    try {
      // Get all referrals where this user is the referrer
      const referralsQuery = query(
        collection(db, 'referrals'),
        where('referrerId', '==', user.uid)
      );
      const referralsSnapshot = await getDocs(referralsQuery);
      
      const breakdown = [];
      for (const referralDoc of referralsSnapshot.docs) {
        const referralData = referralDoc.data();
        const referredArtistId = referralData.referredId;
        
        // Get the referred artist's competition points
        const pointsDoc = await getDoc(doc(db, 'competitionPoints', referredArtistId));
        let pointsFromArtist = 0;
        let artistName = referralData.referredStageName || 'Unknown Artist';
        
        if (pointsDoc.exists()) {
          const pointsData = pointsDoc.data();
          // Each song completion gives +2 points, so divide by 2 to get number of songs
          const songsCompleted = Math.floor((pointsData.points_song_completed || pointsData.songsCompleted || 0) / 2);
          // Each song completion gives the referrer +1 point
          pointsFromArtist = songsCompleted;
        }
        
        // Also get the artist's name from their profile if available
        try {
          const profileDoc = await getDoc(doc(db, 'profiles', referredArtistId));
          if (profileDoc.exists()) {
            const profileData = profileDoc.data();
            artistName = profileData.artist_name || profileData.karaoke_name || artistName;
          }
        } catch (e) {
          // Use the stage name from referral data
        }
        
        if (pointsFromArtist > 0) {
          breakdown.push({
            artistId: referredArtistId,
            artistName: artistName,
            points: pointsFromArtist
          });
        }
      }
      
      // Sort by points (descending)
      breakdown.sort((a, b) => b.points - a.points);
      setReferralBreakdown(breakdown);
    } catch (error) {
      console.error('Error fetching referral breakdown:', error);
    }
  };

  // Fetch breakdown when expanding
  useEffect(() => {
    if (showPointsBreakdown && user?.uid) {
      fetchReferralBreakdown();
    } else {
      setReferralBreakdown([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPointsBreakdown, user?.uid]);

  // countdown ticker
  useEffect(() => {
    const id = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const fetchCompetitionData = async () => {
    try {
      setLoading(true);

      // Check if competition is enabled
      const competitionDoc = await getDoc(doc(db, 'competitions', 'current'));
      if (competitionDoc.exists()) {
        setCompetitionEnabled(competitionDoc.data().enabled);
        setCompetitionDetails({
          prize: competitionDoc.data().prize || competitionDetails.prize,
          rules: competitionDoc.data().rules || competitionDetails.rules,
          startAt: competitionDoc.data().startAt || competitionDetails.startAt,
          endAt: competitionDoc.data().endAt || competitionDetails.endAt
        });
      }

      // Get competition points for current user (detailed)
      try {
        const { getUserCompetitionStats } = await import('@/lib/competition');
        const stats = await getUserCompetitionStats(user.uid);
        setMyPoints(stats.totalPoints || 0);
        setMySelfPoints(stats.songsCompleted || 0);
        setMyReferralPoints(stats.referralsCompleted || 0);
      } catch (e) {
        // Fallback to direct read if helper fails
        const pointsQuery = query(collection(db, 'competitionPoints'), where('userId', '==', user.uid));
        const pointsSnapshot = await getDocs(pointsQuery);
        if (!pointsSnapshot.empty) {
          const myData = pointsSnapshot.docs[0].data();
          setMyPoints(myData.totalPoints || 0);
          setMySelfPoints(myData.points_song_completed || myData.songsCompleted || 0);
          setMyReferralPoints(myData.points_referral || myData.referralsCompleted || 0);
        }
      }

      // Fetch leaderboard
      const leaderboardQuery = query(
        collection(db, 'competitionPoints'),
        where('totalPoints', '>', 0)
      );
      const leaderboardSnapshot = await getDocs(leaderboardQuery);
      
      const leaderboardData = [];
      for (const docSnap of leaderboardSnapshot.docs) {
        const data = docSnap.data();
        const profileDoc = await getDoc(doc(db, 'profiles', data.userId));
        if (profileDoc.exists()) {
          const profileData = profileDoc.data();
          leaderboardData.push({
            userId: data.userId,
            name: profileData.artist_name || profileData.karaoke_name || 'Unknown',
            email: profileData.email || '',
            points: data.totalPoints || 0
          });
        }
      }

      // Sort by points and find my rank
      leaderboardData.sort((a, b) => b.points - a.points);
      setLeaderboard(leaderboardData);
      
      const myRankIndex = leaderboardData.findIndex(item => item.userId === user.uid);
      setMyRank(myRankIndex >= 0 ? myRankIndex + 1 : null);

    } catch (error) {
      console.error('Error fetching competition data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (!competitionEnabled) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <Card className="bg-gray-800 border-gray-700 max-w-md">
          <CardHeader>
            <CardTitle className="text-white flex items-center space-x-2">
              <Radio className="w-6 h-6 text-purple-400" />
              <span>Competition</span>
            </CardTitle>
            <CardDescription>No active competition at this time</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-gray-400 text-center py-8">
              Check back later for exciting competition opportunities!
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const startAtDate = competitionDetails.startAt ? new Date(competitionDetails.startAt) : null;
  const endAtDate = competitionDetails.endAt ? new Date(competitionDetails.endAt) : null;
  const now = new Date(nowTs);
  const beforeStart = startAtDate && now < startAtDate;
  const afterEnd = endAtDate && now > endAtDate;
  const target = beforeStart ? startAtDate : endAtDate;
  const remainingMs = target ? Math.max(0, target.getTime() - now.getTime()) : 0;
  const days = Math.floor(remainingMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((remainingMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));
  const countdownText = `${days}D ${hours}H ${minutes}M`;

  const formatGmt = (d) => {
    if (!d) return '';
    const dd = String(d.getUTCDate()).padStart(2, '0');
    const MM = String(d.getUTCMonth() + 1).padStart(2, '0');
    const yyyy = d.getUTCFullYear();
    const HH = String(d.getUTCHours()).padStart(2, '0');
    const mm2 = String(d.getUTCMinutes()).padStart(2, '0');
    return `${dd}-${MM}-${yyyy} ${HH}:${mm2} (GMT)`;
  };

  return (
    <div className="min-h-screen bg-black text-white p-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
            COMPETITION
          </h1>
        </motion.div>

        {/* Competition Introduction */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="bg-gradient-to-br from-purple-900/40 via-pink-900/30 to-purple-900/40 border-2 border-purple-500/50">
            <CardHeader className="text-center pb-4">
              <div className="flex items-center justify-center mb-3">
                <Sparkles className="w-8 h-8 text-yellow-400 mr-2" />
                <h2 className="text-3xl sm:text-4xl font-bold text-white">
                  WIN A TRIP FOR TWO TO NASHVILLE!
                </h2>
                <Sparkles className="w-8 h-8 text-yellow-400 ml-2" />
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-white/5 rounded-lg p-4 sm:p-6 border border-purple-500/30">
                <p className="text-lg text-white text-center font-medium">
                  Earn the most <span className="text-yellow-400 font-bold">Challenge Points</span> between <span className="text-yellow-400">Dec 1st 2025</span> and <span className="text-yellow-400">Jan 31st 2026</span> to win!
                </p>
              </div>

              {/* Collapsible content from "EARN POINTS BY:" through "YEE HAW!" */}
              {competitionDetailsExpanded && preferenceLoaded && (
                <>
                  <div className="space-y-4">
                    <h3 className="text-xl sm:text-2xl font-bold text-center text-white mb-4 flex items-center justify-center">
                      <Music2 className="w-6 h-6 mr-2 text-purple-400" />
                      EARN POINTS BY:
                    </h3>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Performing Songs */}
                      <div className="bg-purple-900/30 rounded-lg p-4 sm:p-5 border border-purple-600/50">
                        <div className="flex items-start space-x-3 mb-3">
                          <div className="bg-purple-500 rounded-full p-2 flex-shrink-0">
                            <Radio className="w-5 h-5 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-white font-bold text-lg mb-1">Performing Your Own Songs</h4>
                            <p className="text-purple-300 font-semibold text-2xl mb-2">+2 Points</p>
                          </div>
                        </div>
                        <p className="text-gray-300 text-sm sm:text-base">
                          Get <span className="text-purple-300 font-semibold">+2 Points</span> every time you <span className="text-purple-300 font-semibold">Go Live</span> and successfully perform/complete a song request.
                        </p>
                      </div>

                      {/* Referrals */}
                      <div className="bg-pink-900/30 rounded-lg p-4 sm:p-5 border border-pink-600/50">
                        <div className="flex items-start space-x-3 mb-3">
                          <div className="bg-pink-500 rounded-full p-2 flex-shrink-0">
                            <Users className="w-5 h-5 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-white font-bold text-lg mb-1">Refer New Artists</h4>
                            <p className="text-pink-300 font-semibold text-2xl mb-2">+1 Point</p>
                          </div>
                        </div>
                        <p className="text-gray-300 text-sm sm:text-base">
                          Get <span className="text-pink-300 font-semibold">+1 Point</span> for <span className="text-pink-300 font-semibold">each and every song</span> your referred artist completes. They get +2 points for themselves, and you get +1 point as the referrer!
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-yellow-900/30 to-orange-900/30 rounded-lg p-4 sm:p-5 border border-yellow-500/40">
                    <p className="text-white text-base sm:text-lg text-center leading-relaxed mb-3">
                      The more song requests you complete, the more points you earn! And every time your referred artists complete a song, they get +2 points AND you get +1 point as their referrer!
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-center">
                      <div className="flex items-center space-x-2">
                        <span className="text-yellow-400 font-bold text-lg">Go Live</span>
                        <span className="text-gray-300">→</span>
                        <span className="text-gray-300">Receive requests, earn money, and get points!</span>
                      </div>
                    </div>
                    <p className="text-yellow-300 font-semibold text-base sm:text-lg text-center mt-3">
                      And encourage your musically talented friends and colleagues to sign up too!!
                    </p>
                  </div>

                  <div className="text-center pt-2">
                    <p className="text-3xl sm:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-orange-400 to-yellow-400">
                      YEE HAW! 🤠
                    </p>
                  </div>
                </>
              )}

              {/* Collapse button */}
              <div className="text-center">
                <button
                  onClick={toggleCompetitionDetails}
                  className="text-yellow-400 hover:text-yellow-300 text-sm font-medium flex items-center justify-center gap-2 mx-auto transition-colors"
                  aria-expanded={competitionDetailsExpanded}
                >
                  {competitionDetailsExpanded ? (
                    <>
                      <ChevronUp className="w-4 h-4" />
                      Collapse
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-4 h-4" />
                      Expand
                    </>
                  )}
                </button>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Prize, Terms & Rules, and Leaderboard - Always visible */}
        {preferenceLoaded && (
          <>
            {/* Prize Card + Countdown */}
            <Card className="bg-gradient-to-r from-purple-900/20 to-pink-900/20 border-purple-500">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Trophy className="w-6 h-6 text-yellow-400" />
                  <span>The Prize</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xl text-white font-semibold mb-3">{competitionDetails.prize}</p>
                {(startAtDate || endAtDate) && (
                  <div className="flex items-center space-x-3 text-sm flex-wrap">
                    <Timer className="w-4 h-4 text-purple-300" />
                    {beforeStart && <span className="text-purple-200">Starts in {countdownText}</span>}
                    {!beforeStart && !afterEnd && endAtDate && (
                      <span className="text-purple-200">Ends in {countdownText}</span>
                    )}
                    {afterEnd && <span className="text-gray-400">Competition ended</span>}
                    <span className="text-gray-400">| Start: {formatGmt(startAtDate)}</span>
                    {endAtDate && <span className="text-gray-400">End: {formatGmt(endAtDate)}</span>}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Terms and Rules Dropdown */}
            <Card className="bg-gray-800 border-gray-700">
              <button
                onClick={() => setShowTerms(!showTerms)}
                className="w-full"
                aria-expanded={showTerms}
                aria-label="Toggle Terms and Rules"
              >
                <CardHeader className="cursor-pointer hover:bg-gray-700/50 transition-colors rounded-t-lg">
                  <div className="flex items-start sm:items-center justify-between gap-3">
                    <CardTitle className="flex items-start sm:items-center space-x-2 text-left flex-1 min-w-0">
                      <FileText className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5 sm:mt-0" />
                      <span className="text-base sm:text-xl break-words">⭐️ The Nashville Spotlight Challenge: Official Terms & Rules</span>
                    </CardTitle>
                    {showTerms ? (
                      <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0 ml-2 mt-0.5 sm:mt-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0 ml-2 mt-0.5 sm:mt-0" />
                    )}
                  </div>
                </CardHeader>
              </button>
              {showTerms && (
                <CardContent className="pt-0">
                  <div className="space-y-6 text-sm sm:text-base text-gray-300 px-2 sm:px-4 pb-4">
                    {/* Purpose */}
                    <div>
                      <p className="text-gray-400 mb-2">
                        The purpose of this challenge is to reward the artists who are most active in generating revenue through requests and expanding the YSR community with quality referrals.
                      </p>
                    </div>

                    {/* Competition Period */}
                    <div>
                      <h3 className="text-white font-semibold text-base mb-2 flex items-center">
                        <span className="bg-purple-500 w-2 h-2 rounded-full mr-2"></span>
                        1. Competition Period
                      </h3>
                      <p className="text-gray-300">
                        The competition is a high-intensity two-month sprint:
                      </p>
                      <ul className="list-disc list-inside ml-4 mt-2 space-y-1 text-gray-400">
                        <li>Start: December 1st, 2025, 00:00 GMT</li>
                        <li>End: January 31st, 2026, 23:59 GMT</li>
                      </ul>
                    </div>

                    {/* Grand Prize */}
                    <div>
                      <h3 className="text-white font-semibold text-base mb-2 flex items-center">
                        <span className="bg-purple-500 w-2 h-2 rounded-full mr-2"></span>
                        2. The Grand Prize
                      </h3>
                      <p className="text-gray-300 mb-2">
                        The artist with the highest cumulative Challenge Points at the end of the competition period will win a trip for two people to Nashville, Tennessee.
                      </p>
                      <p className="text-gray-300 mb-2">The prize package includes:</p>
                      <ul className="list-disc list-inside ml-4 space-y-1 text-gray-400">
                        <li>Round-trip airfare for two.</li>
                        <li>Accommodation for three (3) nights.</li>
                        <li>A fixed spending allowance or ticket to a major music venue/event (to be confirmed by YSR).</li>
                        <li>The trip dates will be mutually agreed upon by the winner and YSR.</li>
                      </ul>
                    </div>

                    {/* Earning Challenge Points */}
                    <div>
                      <h3 className="text-white font-semibold text-base mb-2 flex items-center">
                        <span className="bg-purple-500 w-2 h-2 rounded-full mr-2"></span>
                        3. Earning Challenge Points
                      </h3>
                      <p className="text-gray-300 mb-3">
                        Points are awarded based on the following activities. All activity must be completed during the competition period (December 1st, 2025, to January 31st, 2026).
                      </p>
                      <div className="bg-purple-900/20 border border-purple-700/50 rounded-lg p-3 sm:p-4 space-y-3">
                        <div className="flex items-start space-x-2 sm:space-x-3">
                          <span className="text-purple-400 font-bold text-base sm:text-lg flex-shrink-0">+2</span>
                          <div className="min-w-0 flex-1">
                            <p className="text-white font-medium text-sm sm:text-base">Complete a Song Request</p>
                            <p className="text-gray-400 text-xs sm:text-sm mt-1 break-words">
                              Get +2 Points every time you Go Live and successfully perform/complete a song request. Unlimited points can be earned using this method.
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start space-x-2 sm:space-x-3">
                          <span className="text-purple-400 font-bold text-base sm:text-lg flex-shrink-0">+1</span>
                          <div className="min-w-0 flex-1">
                            <p className="text-white font-medium text-sm sm:text-base">Successful Artist Referral</p>
                            <p className="text-gray-400 text-xs sm:text-sm mt-1 break-words">
                              Get +1 point for each and every song your referred artist completes. They get +2 points for themselves, and you get +1 point as the referrer - unlimited!
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Referral Rules & Integrity */}
                    <div>
                      <h3 className="text-white font-semibold text-base mb-2 flex items-center">
                        <span className="bg-purple-500 w-2 h-2 rounded-full mr-2"></span>
                        4. Referral Rules & Integrity
                      </h3>
                      <p className="text-gray-300 mb-3">
                        To maintain fairness and focus on quality network growth, these rules are strictly enforced:
                      </p>
                      <div className="space-y-2">
                        <div className="bg-gray-700/30 rounded-lg p-3 sm:p-4">
                          <p className="text-white font-medium mb-1.5 text-sm sm:text-base">Unlimited Referral Points:</p>
                          <p className="text-gray-400 text-xs sm:text-sm break-words">
                            Referral points are awarded for each and every song your referred artist completes - unlimited! Every time they complete a song, they get +2 points and you get +1 point as their referrer.
                          </p>
                        </div>
                        <div className="bg-gray-700/30 rounded-lg p-3 sm:p-4">
                          <p className="text-white font-medium mb-1.5 text-sm sm:text-base">No Self-Referral:</p>
                          <p className="text-gray-400 text-xs sm:text-sm break-words">
                            Artists are prohibited from referring themselves or accounts controlled by them.
                          </p>
                        </div>
                        <div className="bg-red-900/20 border border-red-700/50 rounded-lg p-3 sm:p-4">
                          <p className="text-white font-medium mb-1.5 text-sm sm:text-base">Final Authority:</p>
                          <p className="text-gray-400 text-xs sm:text-sm break-words">
                            YSR reserves the right to audit and verify the legitimacy of all requests and referrals. Any attempt to manipulate the system (including fake accounts or fraudulent transactions) will result in immediate disqualification from the competition and potential suspension from the YSR platform.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Leaderboard and Tiebreakers */}
                    <div>
                      <h3 className="text-white font-semibold text-base mb-2 flex items-center">
                        <span className="bg-purple-500 w-2 h-2 rounded-full mr-2"></span>
                        5. Leaderboard and Tiebreakers
                      </h3>
                      <div className="space-y-2">
                        <div className="bg-gray-700/30 rounded-lg p-3 sm:p-4">
                          <p className="text-white font-medium mb-1.5 text-sm sm:text-base">Leaderboard:</p>
                          <p className="text-gray-400 text-xs sm:text-sm break-words">
                            A visible leaderboard will track the current standings throughout the competition.
                          </p>
                        </div>
                        <div className="bg-gray-700/30 rounded-lg p-3 sm:p-4">
                          <p className="text-white font-medium mb-1.5 text-sm sm:text-base">Tiebreaker:</p>
                          <p className="text-gray-400 text-xs sm:text-sm break-words">
                            In the event of a tie for the top score, the winner will be determined by the total gross revenue earned from requests completed during the competition period.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>

            {/* Tabs */}
            <div className="flex space-x-2">
              <button onClick={() => setActiveTab('leaderboard')} className={`px-3 py-1 rounded ${activeTab==='leaderboard'?'bg-purple-600 text-white':'bg-gray-800 text-gray-300'}`}>Leaderboard</button>
              <button onClick={() => setActiveTab('points')} className={`px-3 py-1 rounded ${activeTab==='points'?'bg-purple-600 text-white':'bg-gray-800 text-gray-300'}`}>Points</button>
              <button onClick={() => setActiveTab('refer')} className={`px-3 py-1 rounded ${activeTab==='refer'?'bg-purple-600 text-white':'bg-gray-800 text-gray-300'}`}>Refer</button>
            </div>

            {activeTab === 'points' && (
              <>
                {/* My Stats */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card className="bg-gray-800 border-gray-700">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-gray-400 text-sm">My Points</p>
                          <p className="text-3xl font-bold text-purple-400">{myPoints}</p>
                          <p className="text-xs text-gray-400 mt-1">Self: {mySelfPoints} • Referrals: {myReferralPoints}</p>
                          <button
                            onClick={() => setShowPointsBreakdown(!showPointsBreakdown)}
                            className="mt-2 text-xs text-purple-400 hover:text-purple-300 transition-colors flex items-center gap-1"
                          >
                            {showPointsBreakdown ? (
                              <>
                                <ChevronUp className="w-3 h-3" />
                                See Less
                              </>
                            ) : (
                              <>
                                <ChevronDown className="w-3 h-3" />
                                See More
                              </>
                            )}
                          </button>
                        </div>
                        <TrendingUp className="w-12 h-12 text-purple-400" />
                      </div>
                      
                      {/* Points Breakdown */}
                      {showPointsBreakdown && (
                        <div className="mt-4 pt-4 border-t border-gray-700 space-y-3">
                          {/* Self Points */}
                          <div className="bg-purple-900/20 rounded-lg p-3 border border-purple-700/30">
                            <p className="text-white font-medium text-sm mb-1">
                              My Requests (+2 points): <span className="text-purple-400 font-bold">{mySelfPoints}</span>
                            </p>
                            <p className="text-xs text-gray-400">
                              {Math.floor(mySelfPoints / 2)} song{Math.floor(mySelfPoints / 2) !== 1 ? 's' : ''} completed
                            </p>
                          </div>
                          
                          {/* Referral Points */}
                          {myReferralPoints > 0 ? (
                            <div className="bg-pink-900/20 rounded-lg p-3 border border-pink-700/30">
                              <p className="text-white font-medium text-sm mb-2">
                                Referrals (+1 point): <span className="text-pink-400 font-bold">{myReferralPoints}</span>
                              </p>
                              {referralBreakdown.length > 0 ? (
                                <div className="space-y-1.5">
                                  {referralBreakdown.map((ref) => (
                                    <div key={ref.artistId} className="flex items-center justify-between text-xs bg-gray-800/50 rounded px-2 py-1">
                                      <span className="text-gray-300 truncate flex-1 mr-2">{ref.artistName}</span>
                                      <span className="text-pink-400 font-semibold">{ref.points} pt{ref.points !== 1 ? 's' : ''}</span>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-xs text-gray-400">Loading referral breakdown...</p>
                              )}
                            </div>
                          ) : (
                            <div className="bg-pink-900/20 rounded-lg p-3 border border-pink-700/30">
                              <p className="text-white font-medium text-sm mb-1">
                                Referrals (+1 point): <span className="text-pink-400 font-bold">0</span>
                              </p>
                              <p className="text-xs text-gray-400">No referral points yet</p>
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="bg-gray-800 border-gray-700">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-gray-400 text-sm">My Rank</p>
                          <p className="text-3xl font-bold text-pink-400">
                            {myRank ? `#${myRank}` : '—'}
                          </p>
                        </div>
                        <Medal className="w-12 h-12 text-pink-400" />
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}

            {activeTab === 'refer' && (
              <>
                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Share2 className="w-5 h-5 text-pink-400" />
                      <span>Refer Artists</span>
                    </CardTitle>
                    <CardDescription>Share your referral link to earn +1 point for each song your referred artists complete</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {referralCode ? (
                      <>
                        <div className="bg-gradient-to-r from-pink-900/30 to-purple-900/30 rounded-lg p-6 border border-pink-500/30">
                          <div className="space-y-4">
                            <div>
                              <label className="text-sm font-medium text-gray-300 mb-2 block">Your Referral Link</label>
                              <div className="flex items-center space-x-2">
                                <Input
                                  value={referralUrl}
                                  readOnly
                                  className="bg-gray-700 border-gray-600 text-white font-mono text-sm"
                                />
                                <Button
                                  onClick={() => {
                                    navigator.clipboard.writeText(referralUrl);
                                    toast({
                                      title: "Copied!",
                                      description: "Referral link copied to clipboard",
                                    });
                                  }}
                                  variant="outline"
                                  size="sm"
                                  className="border-pink-500 text-pink-400 hover:bg-pink-500 hover:text-white"
                                >
                                  <Copy className="w-4 h-4 mr-1" />
                                  Copy
                                </Button>
                              </div>
                            </div>
                            
                            <div className="flex flex-wrap gap-2">
                              <Button
                                onClick={() => {
                                  if (navigator.share) {
                                    navigator.share({
                                      title: 'Join YSR as an Artist!',
                                      text: 'Check out YSR - Your Song Request! Join me and start earning.',
                                      url: referralUrl,
                                    });
                                  } else {
                                    navigator.clipboard.writeText(referralUrl);
                                    toast({
                                      title: "Copied!",
                                      description: "Referral link copied to clipboard",
                                    });
                                  }
                                }}
                                className="bg-pink-600 hover:bg-pink-700 text-white"
                              >
                                <Share2 className="w-4 h-4 mr-2" />
                                Share Link
                              </Button>
                            </div>
                          </div>
                        </div>

                        <div className="bg-gray-700/30 rounded-lg p-4 border border-gray-600">
                          <p className="text-sm text-gray-300">
                            <span className="font-semibold text-pink-400">How it works:</span> Share your referral link with other artists. When they sign up using your link and complete songs, you'll earn <span className="font-bold text-pink-400">+1 point for each song</span> they complete!
                          </p>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-8">
                        <Users className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                        <p className="text-gray-400">Loading your referral code...</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}

            {activeTab === 'leaderboard' && (
              <>
                {/* Leaderboard */}
                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Users className="w-5 h-5" />
                      <span>Leaderboard</span>
                    </CardTitle>
                    <CardDescription>Top artists in the competition</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {leaderboard.slice(0, 10).map((artist, index) => (
                        <motion.div
                          key={artist.userId}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className={`flex items-center justify-between p-4 rounded-lg ${
                            artist.userId === user.uid
                              ? 'bg-purple-900/30 border border-purple-500'
                              : 'bg-gray-700/50'
                          }`}
                        >
                          <div className="flex items-center space-x-4">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                              index === 0 ? 'bg-gradient-to-r from-yellow-400 to-orange-400 text-white' :
                              index === 1 ? 'bg-gradient-to-r from-gray-300 to-gray-400 text-gray-800' :
                              index === 2 ? 'bg-gradient-to-r from-orange-400 to-amber-600 text-white' :
                              'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                            }`}>
                              {index + 1}
                            </div>
                            <div>
                              <h3 className="font-semibold text-white">{artist.name}</h3>
                              {artist.userId === user.uid && (
                                <Badge variant="outline" className="border-purple-500 text-purple-400 mt-1">
                                  You
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-purple-400">{artist.points}</p>
                            <p className="text-xs text-gray-400">points</p>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default ArtistCompetitionPage;

